/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { CircularProgress, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectGpsSaMapLayersChecked,
  selectGpsSaMapLayersRequestFinished,
  selectIsRequestingGpsSaMapLayers,
  selectGpsSaMapLayersRequestError,
} from '../../../store';
import { useSelectorWithMapId } from '../map/BaseMapContainer';
import { AlternativeContainer, StyledButton, StyledSection } from './CommonOverlayComponents';
import GpsSaMapLayerControl from './GpsSaMapLayerControl';
import { Warning } from '@mui/icons-material';
import { cancelRequestGpsSaMapLayersPoll, requestGpsSaMapLayers } from '../../../store/gpsSa/actions';
import GpsSaMapLayerControlCheckboxes from './GpsSaMapLayerControlCheckboxes';

const GPS_SA_OVERLAYS_SECTION_HEIGHT = 120;

const GpsSaOverlays = () => {
  const dispatch = useDispatch();

  const isMapLayersRequestFinished = useSelector(selectGpsSaMapLayersRequestFinished, shallowEqual);
  const isRequestingMapLayers = useSelector(selectIsRequestingGpsSaMapLayers, shallowEqual);
  const requestMapLayersError = useSelector(selectGpsSaMapLayersRequestError, shallowEqual);

  const gpsSaEnabled = useSelectorWithMapId(selectGpsSaMapLayersChecked);

  const overlayBodyComponent = useMemo(() => {
    if (!gpsSaEnabled) {
      // If GPS SA is not enabled, don't render anything in the section body
      return null;
    } else if (isRequestingMapLayers) {
      // If the map layers are still being requested, show a loading spinner
      return (
        <AlternativeContainer>
          <CircularProgress className="progress" />
        </AlternativeContainer>
      );
    } else if (isMapLayersRequestFinished && requestMapLayersError) {
      // Cancel map layer polling if the request fails
      dispatch(cancelRequestGpsSaMapLayersPoll());

      // If there was an error requesting the map layers, show an error message
      return (
        <AlternativeContainer height={GPS_SA_OVERLAYS_SECTION_HEIGHT}>
          <Warning />
          <Typography>
            Error Retrieving GPS Position <br />
            Accuracy Data
          </Typography>
          <StyledButton
            color={'primary'}
            data-test="gpsSa-overlays--retry"
            onClick={() => {
              // Request the GPS SA map layers if they are turned on
              dispatch(requestGpsSaMapLayers());
            }}
          >
            RETRY
          </StyledButton>
        </AlternativeContainer>
      );
    } else {
      // If the map layers have been successfully requested, render the checkboxes for the different accuracy levels
      return <GpsSaMapLayerControlCheckboxes />;
    }
  }, [gpsSaEnabled, isRequestingMapLayers, isMapLayersRequestFinished, requestMapLayersError, dispatch]);

  return (
    <StyledSection>
      <GpsSaMapLayerControl />

      {overlayBodyComponent}
    </StyledSection>
  );
};

export default GpsSaOverlays;
